import React, { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Assignment } from '@material-ui/icons';
import Button from 'components/CustomButtons/Button.js';

//tabla y card components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardIcon from 'components/Card/CardIcon.js';
import CardHeader from 'components/Card/CardHeader.js';
import { cardTitle } from 'assets/jss/material-dashboard-pro-react.js';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';

import Tesseract from 'tesseract.js';

//servicios
import { wsPost } from 'services/WService';

//modal
import { WithApp } from 'contexts/appContext';
import { useToasty } from 'hooks/Toasty';
import { Toasty } from 'hooks/Toasty';

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: '15px',
    marginBottom: '0px'
  }
};
const useStyles = makeStyles(styles);

const MONTH_MAP = {
  'ene': '01', 'enero': '01',
  'feb': '02', 'febrero': '02',
  'mar': '03', 'marzo': '03',
  'abr': '04', 'abril': '04',
  'may': '05', 'mayo': '05',
  'jun': '06', 'junio': '06',
  'jul': '07', 'julio': '07',
  'ago': '08', 'agosto': '08',
  'sep': '09', 'sept': '09', 'septiembre': '09',
  'oct': '10', 'octubre': '10',
  'nov': '11', 'noviembre': '11',
  'dic': '12', 'diciembre': '12',
};

function normalizeText(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanForNumbers(text) {
  return text
    .replace(/[oO]/g, '0')  // O → 0
    .replace(/[lI|]/g, '1') // l, I, | → 1
    .replace(/[sS]/g, '5'); // S → 5 en contexto numérico
}


function detectPaymentMethod(text) {
  const normalized = normalizeText(text);

  if (normalized.includes('yapeaste') || normalized.includes('yapeo')) {
    return { method: 'yape', confidence: 98 };
  }
  if (normalized.includes('yape')) {
    return { method: 'yape', confidence: 95 };
  }
  if (normalized.includes('plin')) {
    return { method: 'plin', confidence: 95 };
  }
  if (normalized.includes('interbank') || normalized.includes('bcp') || normalized.includes('bbva')) {
    return { method: 'transferencia', confidence: 85 };
  }
  return { method: null, confidence: 0 };
}

function extractAmount(text, isYape = false) {
  // Limpiar caracteres comunes de OCR mal leído
  let cleaned = text
    .replace(/[oO]/g, '0')
    .replace(/[lI|]/g, '1')
    .replace(/\$/g, 'S/')  // $ → S/
    .replace(/5\//g, 'S/') // 5/ → S/ (OCR confunde S con 5)
    .replace(/s\//gi, 'S/') // s/ → S/
    .replace(/«/g, 'S/')    // « → S/ (común en OCR)
    .replace(/»/g, '')      // Eliminar »
    .replace(/§/g, 'S/');   // § → S/

  if (isYape) {
    // DETECTOR: ¿Es un voucher recortado sin "Yapeaste"?
    const isClippedVoucher = !cleaned.toLowerCase().includes('yapeaste') && 
                             cleaned.toLowerCase().includes('codigo de seguridad');

    console.log(`Voucher ${isClippedVoucher ? 'RECORTADO' : 'COMPLETO'} detectado`);

    if (isClippedVoucher) {
      // PATRONES PARA VOUCHER RECORTADO
      const clippedPatterns = [
        // Patrón 1: "CÓDIGO DE SEGURIDAD" seguido de "S/ número" o solo número
        /codigo\s+de\s+seguridad[^\n]*\n[^\d]*[S5s]?\s*\/?\s*(\d{1,4}(?:[.,]\d{2})?)/gis,
        
        // Patrón 2: Número antes de un nombre (típico: monto antes del destinatario)
        /(\d{1,4}(?:[.,]\d{2})?)\s*\n\s*([A-ZÁÉÍÓÚ][a-záéíóú]+\s+[A-Za-záéíóú\*]+)/g,
        
        // Patrón 3: "53 03 feb" → extraer "53" (OCR lee "S/ 5" como "53")
        /(\d{1,2})\s+\d{1,2}\s+(?:ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)/gi,

        // Patrón 4: Número suelto después de "CÓDIGO DE SEGURIDAD"
        /codigo\s+de\s+seguridad[^\n]*\n[^\d]*(\d{1,4})/gis,

        // Patrón 5: Línea que solo tiene un número de 1-4 dígitos
        /^\s*(\d{1,4})\s*$/gm,
      ];

      for (const pattern of clippedPatterns) {
        const matches = [...cleaned.matchAll(pattern)];
        for (const match of matches) {
          let amountStr = match[1].replace(',', '.');
          let amount = parseFloat(amountStr);

          // CASO ESPECIAL: Si es "53" pegado a fecha, probablemente sea "5"
          if (match[0].includes('53') && /\d{1,2}\s+(?:ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)/i.test(match[0])) {
            amountStr = amountStr.replace('53', '5');
            amount = parseFloat(amountStr);
            console.log(`Corrigiendo "53" → "5" por contexto de fecha`);
          }

          // Validar
          if (!isNaN(amount) && amount > 0 && amount < 50000 &&
              !amountStr.match(/^20\d{2}$/) && // No es año
              !amountStr.match(/^\d{7,}$/) &&  // No es número de operación
              !amountStr.match(/^[789]\d{2}$/) // No es código de 3 dígitos tipo "780"
          ) {
            console.log(`Monto extraído (recortado): ${amount}`);
            return { amount, confidence: 80 };
          }
        }
      }
    } else {
      // PATRONES PARA VOUCHER COMPLETO (con "Yapeaste")
      // PRIMERO: Ignorar números entre "Yapeaste" y "Compartir" (son íconos)
      const yapePatterns = [
        // PRIORIDAD 1: "S/ 5" en su propia línea (formato típico de Yape)
        /^[S5s]\s*\/\s*(\d{1,4}(?:[.,]\d{2})?)\s*$/gm,
        
        // PRIORIDAD 2: "S/ 5" después de "Compartir" (en línea nueva)
        /compartir[^\n]*\n\s*[S5s]?\s*\/?\s*(\d{1,4}(?:[.,]\d{2})?)/gis,
        
        // PRIORIDAD 3: Línea que solo tiene "S/ número" sin más texto
        /^\s*[S5s]\s*\/\s*(\d{1,4}(?:[.,]\d{2})?)\s*$/gm,

        // PRIORIDAD 4: Buscar después de salto de línea tras "Yapeaste...Compartir"
        /yapeaste.*?compartir[^\n]*\n[^\n]*?(\d{1,4}(?:[.,]\d{2})?)/gis,

        // Patrones estándar (con menor prioridad)
        /S\/\s*(\d{1,6}(?:[.,]\d{2})?)/gi,
        /[S5]\/\s*(\d{1,6}(?:[.,]\d{2})?)/gi,
      ];

      for (const pattern of yapePatterns) {
        const matches = [...cleaned.matchAll(pattern)];
        for (const match of matches) {
          const amountStr = match[1].replace(',', '.');
          const amount = parseFloat(amountStr);

          // Validar que sea un monto razonable
          // IMPORTANTE: Excluir números que estén entre "Yapeaste" y "Compartir"
          const matchIndex = match.index;
          const yapeIndex = cleaned.toLowerCase().indexOf('yapeaste');
          const compartirIndex = cleaned.toLowerCase().indexOf('compartir');
          
          // Si el número está entre "Yapeaste" y "Compartir", ignorarlo (es el ícono)
          if (yapeIndex !== -1 && compartirIndex !== -1 && 
              matchIndex > yapeIndex && matchIndex < compartirIndex) {
            continue; // Saltar este match
          }

          if (!isNaN(amount) && amount > 0 && amount < 50000 &&
              !amountStr.match(/^20\d{2}$/) && // No es año
              !amountStr.match(/^\d{7,}$/) &&  // No es número de operación
              !amountStr.match(/^\d{3}$/)      // No es código de 3 dígitos
          ) {
            return { amount, confidence: 85 };
          }
        }
      }

      // FALLBACK: Buscar números en líneas después de "Compartir"
      const lines = text.split('\n');
      const compartirLineIdx = lines.findIndex(l => l.toLowerCase().includes('compartir'));
      
      if (compartirLineIdx !== -1) {
        // Buscar en las siguientes 3 líneas
        for (let i = compartirLineIdx + 1; i < Math.min(compartirLineIdx + 4, lines.length); i++) {
          const line = lines[i];
          // Buscar patrón "S/ número" o solo "número"
          const lineMatch = /[S5s]?\s*\/?\s*(\d{1,4}(?:[.,]\d{2})?)/i.exec(line);
          if (lineMatch) {
            const amount = parseFloat(lineMatch[1].replace(',', '.'));
            if (!isNaN(amount) && amount > 0 && amount < 10000 &&
                line.length < 30) { // La línea del monto suele ser corta
              return { amount, confidence: 80 };
            }
          }
        }
      }
    }

    // FALLBACK UNIVERSAL: Buscar número antes del nombre
    const nameMatch = /([A-ZÁÉÍÓÚ][a-záéíóú]+\s+[A-Za-záéíóú\*]+)/g.exec(text);
    if (nameMatch) {
      const beforeName = text.substring(0, nameMatch.index);
      const numMatch = /(\d{1,4}(?:[.,]\d{2})?)\s*$/.exec(beforeName);
      if (numMatch) {
        const amount = parseFloat(numMatch[1].replace(',', '.'));
        if (!isNaN(amount) && amount > 0 && amount < 10000) {
          return { amount, confidence: 70 };
        }
      }
    }
  }

  // Patrón genérico S/
  const solesPatterns = [
    /S\/\.?\s*(\d{1,6}(?:[.,]\d{2})?)/gi,
    /[S5]\/\.?\s*(\d{1,6}(?:[.,]\d{2})?)/gi,
    /monto[:\s]*S?\/?\s*(\d{1,6}(?:[.,]\d{2})?)/gi,
  ];

  for (const pattern of solesPatterns) {
    const matches = [...cleaned.matchAll(pattern)];
    if (matches.length > 0) {
      const amount = parseFloat(matches[0][1].replace(',', '.'));
      if (!isNaN(amount) && amount > 0) {
        return { amount, confidence: 75 };
      }
    }
  }

  return { amount: null, confidence: 0 };
}

function extractOperationNumber(text) {
  const patterns = [
    /nro\.?\s*(?:de\s*)?operacion[:\s]*(\d{6,12})/gi,
    /operacion[:\s]*(\d{6,12})/gi,
    /codigo[:\s]*(\d{6,12})/gi,
  ];

  for (const pattern of patterns) {
    const match = pattern.exec(normalizeText(text));
    if (match && match[1]) {
      return { operationNumber: match[1], confidence: 95 };
    }
  }

  // Fallback: buscar números largos (7-10 dígitos)
  const lines = text.split('\n');
  for (const line of lines) {
    if (line.toLowerCase().includes('celular')) continue;
    const numMatch = /\b(\d{7,10})\b/.exec(line);
    if (numMatch && !numMatch[1].startsWith('9')) {
      return { operationNumber: numMatch[1], confidence: 60 };
    }
  }

  return { operationNumber: null, confidence: 0 };
}

function extractDate(text) {
  // Patrón: "03 feb. 2026" o "03 feb 2026"
  const spanishPattern = /(\d{1,2})\s*(?:de\s*)?([a-záéíóú]+)\.?\s*(?:de\s*)?(\d{4})/gi;
  const match = spanishPattern.exec(text);

  if (match) {
    const day = match[1].padStart(2, '0');
    const monthStr = match[2].toLowerCase().replace('.', '');
    const year = match[3];
    const month = MONTH_MAP[monthStr];

    if (month && parseInt(day) <= 31) {
      return { date: `${year}-${month}-${day}`, confidence: 90 };
    }
  }

  // Patrón: "03/02/2026"
  const numericPattern = /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/;
  const numMatch = numericPattern.exec(text);
  if (numMatch) {
    const day = numMatch[1].padStart(2, '0');
    const month = numMatch[2].padStart(2, '0');
    const year = numMatch[3];
    return { date: `${year}-${month}-${day}`, confidence: 85 };
  }

  return { date: null, confidence: 0 };
}

function extractMerchantName(text, isYape = false) {
  if (isYape) {
    const isClippedVoucher = !text.toLowerCase().includes('yapeaste') && 
                             text.toLowerCase().includes('codigo de seguridad');

    if (isClippedVoucher) {
      // Para voucher recortado: buscar nombre después de "CÓDIGO DE SEGURIDAD" o después del monto
      const patterns = [
        // Patrón 1: Nombre después de un número (monto) 
        /\d{1,4}\s*\n?\s*([A-ZÁÉÍÓÚÑ][a-záéíóúñA-ZÁÉÍÓÚÑ\s\*\.]+?)(?:\n|$)/,
        
        // Patrón 2: Nombre en línea después de CÓDIGO DE SEGURIDAD
        /codigo\s+de\s+seguridad[^\n]*\n[^\n]*\n\s*([A-ZÁÉÍÓÚÑ][a-záéíóúñA-ZÁÉÍÓÚÑ\s\*\.]+?)(?:\n|$)/is,
        
        // Patrón 3: Primera línea con nombre propio (mayúscula + minúsculas + espacio + más texto)
        /([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+\s+[A-Za-záéíóúñ\*]+)/,
      ];

      for (const pattern of patterns) {
        const match = pattern.exec(text);
        if (match && match[1]) {
          let name = match[1].trim().replace(/\s+/g, ' ');
          const skipWords = ['codigo', 'seguridad', 'datos', 'transaccion', 'celular', 'operacion', 'destino', 'feb', 'ene', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
          
          // Verificar que no sea una palabra a saltar y tenga formato de nombre
          if (!skipWords.some(w => name.toLowerCase().includes(w)) && 
              name.length >= 3 && 
              name.length <= 50 &&
              /[A-ZÁÉÍÓÚÑ]/.test(name)) { // Tiene al menos una mayúscula
            return { merchantName: name, confidence: 85 };
          }
        }
      }
    } else {
      // Para voucher completo: el nombre aparece después del monto
      const patterns = [
        /(?:[\d.,]+)\s*\n\s*([A-ZÁÉÍÓÚÑ][a-záéíóúñA-ZÁÉÍÓÚÑ\s\*\.]+?)(?:\n|$)/,
        /yapeaste.*?[\d.,]+\s*\n?\s*([A-ZÁÉÍÓÚÑ][a-záéíóúñ\s\*\.]+)/is,
      ];

      for (const pattern of patterns) {
        const match = pattern.exec(text);
        if (match && match[1]) {
          let name = match[1].trim().replace(/\s+/g, ' ');
          const skipWords = ['codigo', 'seguridad', 'datos', 'transaccion', 'celular', 'operacion'];
          if (!skipWords.some(w => name.toLowerCase().includes(w)) && name.length >= 3 && name.length <= 50) {
            return { merchantName: name, confidence: 80 };
          }
        }
      }
    }
  }

  return { merchantName: null, confidence: 0 };
}

// Función principal de parsing
export function parseReceiptOCR(ocrText) {
  const paymentResult = detectPaymentMethod(ocrText);
  const isYape = paymentResult.method === 'yape';
  const isPlin = paymentResult.method === 'plin';

  console.log(`\n--- Parsing Receipt ---`);
  console.log(`Método de pago detectado: ${paymentResult.method} (${paymentResult.confidence}%)`);

  const amountResult = extractAmount(ocrText, isYape || isPlin);
  console.log(`Monto extraído: ${amountResult.amount} (${amountResult.confidence}%)`);

  const operationResult = extractOperationNumber(ocrText);
  console.log(`Nro. operación: ${operationResult.operationNumber} (${operationResult.confidence}%)`);

  const dateResult = extractDate(ocrText);
  console.log(`Fecha: ${dateResult.date} (${dateResult.confidence}%)`);

  const merchantResult = extractMerchantName(ocrText, isYape || isPlin);
  console.log(`Comercio: ${merchantResult.merchantName} (${merchantResult.confidence}%)`);
  console.log(`--- Fin Parsing ---\n`);

  return {
    amount: amountResult.amount,
    operationNumber: operationResult.operationNumber,
    date: dateResult.date,
    merchantName: merchantResult.merchantName,
    paymentMethod: paymentResult.method,
    confidence: {
      amount: amountResult.confidence,
      operationNumber: operationResult.confidence,
      date: dateResult.confidence,
      merchantName: merchantResult.confidence,
      paymentMethod: paymentResult.confidence,
    }
  };
}

export default WithApp(function SaleForceDetail(props) {
  const classes = useStyles();
  const [notify, showNotify] = useToasty();
  const [dataLoading, setDataLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [parsedResults, setParsedResults] = useState([]);

  const [ocrText, setOcrText] = useState('');

  const inputFolderRef = useRef(null);

  const handleSelectFolder = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleProcessImages = async () => {
    if (!files.length) {
      showNotify('Selecciona una carpeta con imágenes', 'error');
      return;
    }
    setDataLoading(true);
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images[]', file);
    });
    let response = await wsPost('utl/process-image', formData);
    setDataLoading(false);
    if (response.success) {
      showNotify(response.message);
    } else {
      showNotify(response.message, 'error');
    }
  }

  const preprocessImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        // ESCALAR más agresivamente para capturas pequeñas
        const scale = img.width < 800 ? 3 : img.width < 1200 ? 2 : 1.5;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        // Usar interpolación de alta calidad
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // DETECTAR si tiene fondo oscuro (como Yape morado)
        let sumBrightness = 0;
        let pixelCount = 0;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const brightness = (r + g + b) / 3;
          sumBrightness += brightness;
          pixelCount++;
        }
        const avgBrightness = sumBrightness / pixelCount;
        const isDarkBackground = avgBrightness < 180;

        console.log(`Brillo promedio: ${avgBrightness.toFixed(1)} - Fondo ${isDarkBackground ? 'oscuro' : 'claro'}`);

        // Convertir a escala de grises primero
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          // Escala de grises con peso
          let gray = 0.299 * r + 0.587 * g + 0.114 * b;
          
          // Si es fondo oscuro, INVERTIR para que Tesseract lo lea mejor
          if (isDarkBackground) {
            gray = 255 - gray;
          }

          data[i] = gray;
          data[i + 1] = gray;
          data[i + 2] = gray;
        }

        // Aplicar filtro de sharpening para mejorar bordes del texto
        const sharpenKernel = [
          0, -1, 0,
          -1, 5, -1,
          0, -1, 0
        ];

        const tempData = new Uint8ClampedArray(data);
        const width = canvas.width;
        const height = canvas.height;

        for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
            let sum = 0;
            for (let ky = -1; ky <= 1; ky++) {
              for (let kx = -1; kx <= 1; kx++) {
                const idx = ((y + ky) * width + (x + kx)) * 4;
                const kernelIdx = (ky + 1) * 3 + (kx + 1);
                sum += tempData[idx] * sharpenKernel[kernelIdx];
              }
            }
            const idx = (y * width + x) * 4;
            const value = Math.max(0, Math.min(255, sum));
            data[idx] = value;
            data[idx + 1] = value;
            data[idx + 2] = value;
          }
        }

        // Ahora aplicar contraste y binarización
        for (let i = 0; i < data.length; i += 4) {
          let gray = data[i];

          // Aumentar contraste más agresivamente
          gray = ((gray - 128) * 2.2) + 128;
          gray = Math.max(0, Math.min(255, gray));

          // Binarización con threshold ajustado
          const threshold = gray > 128 ? 255 : 0;

          data[i] = threshold;
          data[i + 1] = threshold;
          data[i + 2] = threshold;
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };

      img.src = URL.createObjectURL(file);
    });
  };


  const handleOCR = async () => {
    setDataLoading(true);
    setParsedResults([]);
    let allParsedData = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const processedImage = await preprocessImage(files[i]);

        const result = await Tesseract.recognize(
          processedImage,
          'spa+eng',
          {
            logger: m => {
              if (m.status === 'recognizing text') {
                console.log(`Imagen ${i + 1} - Progreso: ${Math.round(m.progress * 100)}%`);
              }
            },
            // Configuración mejorada para Tesseract
            tessedit_pageseg_mode: '3', // Fully automatic page segmentation (mejor para layouts variados)
            tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyzÁÉÍÓÚáéíóúñÑ0123456789.,:/!¡*- ',
            preserve_interword_spaces: '1',
          }
        );

        const ocrText = result.data.text.trim();
        console.log(`\n========== OCR Imagen ${i + 1}: ${files[i].name} ==========`);
        console.log('Texto completo:', ocrText);
        console.log('\nLíneas detectadas:');
        ocrText.split('\n').forEach((line, idx) => {
          console.log(`  ${idx}: "${line}"`);
        });

        const parsed = parseReceiptOCR(ocrText);
        allParsedData.push({
          fileName: files[i].name,
          rawText: ocrText,
          ...parsed
        });
      }

      setParsedResults(allParsedData);

    } catch (e) {
      console.error(e);
    } finally {
      setDataLoading(false);
    }
  };


  return (
    <GridContainer>
      <Toasty action={notify} />
      <GridItem xs={12}>
        <Card>
          <Toasty action={notify} />
          <CardHeader icon>
            <CardIcon color={props.themeContext.buttonColor}>
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              Escaneo de imágenes a Excel {dataLoading && <LoadingSpinner />}
            </h4>
          </CardHeader>

          <CardBody>
            <input
              type="file"
              ref={inputFolderRef}
              style={{ display: 'none' }}
              webkitdirectory="true"
              directory="true"
              multiple
              onChange={handleSelectFolder}
            />
            <Button
              color="success"
              onClick={() => inputFolderRef.current.click()}
            >
              Importar carpeta de imágenes
            </Button>

            <Button
              color="primary"
              disabled={!files.length || dataLoading}
              onClick={handleOCR}
              style={{ marginTop: 10 }}
            >
              Escanear texto (OCR)
            </Button>
            {parsedResults && parsedResults.map((result, idx) => (
              <Card key={idx} style={{ marginTop: 15 }}>
                <CardBody>
                  <h5>Imagen {idx + 1}: {result.fileName}</h5>

                  {/* Campos detectados */}
                  <div style={{
                    background: '#e8f5e9',
                    padding: 15,
                    borderRadius: 8,
                    marginBottom: 15
                  }}>
                    <h6 style={{ color: '#2e7d32', marginBottom: 10 }}>
                      ✨ Campos detectados automáticamente
                    </h6>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {/* AGREGAR ESTO para mostrar el monto */}
                      {result.amount && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>Monto:</span>
                          <strong>S/ {result.amount.toFixed(2)}</strong>
                          <span style={{
                            background: result.confidence.amount >= 70 ? '#c8e6c9' : '#fff9c4',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 12
                          }}>
                            {result.confidence.amount}%
                          </span>
                        </div>
                      )}

                      {result.date && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>Fecha:</span>
                          <strong>{result.date}</strong>
                          <span style={{
                            background: result.confidence.date >= 70 ? '#c8e6c9' : '#fff9c4',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 12
                          }}>
                            {result.confidence.date}%
                          </span>
                        </div>
                      )}

                      {result.merchantName && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>Comercio:</span>
                          <strong>{result.merchantName}</strong>
                          <span style={{
                            background: result.confidence.merchantName >= 70 ? '#c8e6c9' : '#fff9c4',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 12
                          }}>
                            {result.confidence.merchantName}%
                          </span>
                        </div>
                      )}

                      {result.paymentMethod && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>Método:</span>
                          <strong style={{ textTransform: 'capitalize' }}>{result.paymentMethod}</strong>
                          <span style={{
                            background: result.confidence.paymentMethod >= 70 ? '#c8e6c9' : '#fff9c4',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 12
                          }}>
                            {result.confidence.paymentMethod}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Texto crudo (colapsable) */}
                  <details>
                    <summary style={{ cursor: 'pointer', color: '#666' }}>
                      Ver texto extraído (OCR)
                    </summary>
                    <textarea
                      value={result.rawText}
                      readOnly
                      rows={8}
                      style={{
                        width: '100%',
                        marginTop: 10,
                        padding: 10,
                        fontFamily: 'monospace',
                        fontSize: 12,
                      }}
                    />
                  </details>
                </CardBody>
              </Card>
            ))}
            {files.length > 0 && (
              <p style={{ marginTop: 10 }}>
                {files.length} archivos seleccionados
              </p>
            )}

            {/* <Button
              color="primary"
              disabled={!files.length || dataLoading}
              onClick={handleProcessImages}
              style={{ marginTop: 10 }}
            >
              Procesar imágenes
            </Button> */}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
});
